/** Compound interest and SIP projections. */
export interface YearRow { year: number; contributed: number; interest: number; balance: number }

export interface CompoundInput { principal: number; monthly: number; ratePct: number; years: number; perYear: number; atStart: boolean; inflationPct?: number }
export interface CompoundResult { balance: number; contributed: number; interest: number; todaysDollars: number; rows: YearRow[] }

function compoundAt(i: CompoundInput, t: number): number {
  const n = i.perYear, r = i.ratePct / 100 / n, periods = n * t;
  const fvP = i.principal * Math.pow(1 + r, periods);
  const per = i.monthly * (12 / n);
  const ann = r > 0 ? per * ((Math.pow(1 + r, periods) - 1) / r) : per * periods;
  return fvP + (i.atStart ? ann * (1 + r) : ann);
}

export function compound(i: CompoundInput): CompoundResult {
  const whole = Math.floor(i.years + 1e-9);
  const rows: YearRow[] = [];
  for (let y = 1; y <= whole; y++) {
    const bal = compoundAt(i, y), contributed = i.principal + i.monthly * 12 * y;
    rows.push({ year: y, contributed, interest: bal - contributed, balance: bal });
  }
  const balance = compoundAt(i, i.years), contributed = i.principal + i.monthly * 12 * i.years;
  return { balance, contributed, interest: balance - contributed, todaysDollars: balance / Math.pow(1 + (i.inflationPct ?? 0) / 100, i.years), rows };
}

export interface SipInput { monthly: number; ratePct: number; years: number; stepUpPct: number; lumpsum: number; inflationPct: number }
export interface SipResult { balance: number; invested: number; gains: number; todaysDollars: number; rows: YearRow[] }

/** Payment at the start of each month, monthly rate = annual/12 (the convention used by Indian mutual-fund calculators). */
export function sip(i: SipInput): SipResult {
  const months = Math.max(0, Math.round(i.years * 12)), r = i.ratePct / 100 / 12;
  let bal = i.lumpsum, invested = i.lumpsum;
  const rows: YearRow[] = [];
  for (let m = 0; m < months; m++) {
    const c = i.monthly * Math.pow(1 + i.stepUpPct / 100, Math.floor(m / 12));
    bal = (bal + c) * (1 + r); invested += c;
    if ((m + 1) % 12 === 0 || m === months - 1) rows.push({ year: Math.ceil((m + 1) / 12), contributed: invested, interest: bal - invested, balance: bal });
  }
  return { balance: bal, invested, gains: bal - invested, todaysDollars: bal / Math.pow(1 + i.inflationPct / 100, months / 12), rows };
}

export interface DiscountResult { original: number; saved: number; final: number; effectivePct: number; tax: number; total: number }
/** Up to two stacked percentages, then optional tax on the discounted price. */
export function discount(original: number, d1: number, d2: number, taxPct: number): DiscountResult {
  const final = original * (1 - d1 / 100) * (1 - d2 / 100);
  const tax = (final * taxPct) / 100;
  return { original, saved: original - final, final, effectivePct: original > 0 ? ((original - final) / original) * 100 : 0, tax, total: final + tax };
}
export function discountFromPrices(original: number, sale: number): { pct: number; saved: number } {
  return { pct: original > 0 ? ((original - sale) / original) * 100 : 0, saved: original - sale };
}
